'use client';

import { useState, useRef } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Loader2, Github } from 'lucide-react';
import { cn } from '@/lib/utils';
import { triggerConfetti } from '@/lib/confetti';

type ButtonVariant = 'header' | 'hero' | 'faq';

interface GitHubSignInButtonProps {
  variant?: ButtonVariant;
  className?: string;
  showLabelOnHover?: boolean;
}

export function GitHubSignInButton({ 
  variant = 'header',
  className,
  showLabelOnHover = true
}: GitHubSignInButtonProps) {
  const { signInWithGitHub } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      await signInWithGitHub();
    } catch (error) {
      console.error('Error signing in:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const buttonClassName = cn(
    "ring-offset-background",
    "focus-visible:ring-offset-2",
    "inline-flex items-center justify-center whitespace-nowrap",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    "disabled:pointer-events-none disabled:opacity-50",
    "shadow-sm hover:shadow-md transition-all duration-300",
    "bg-background hover:bg-background/90",
    "text-foreground",
    "hover:scale-[1.02]",
    variant === "header" && "rounded-full border border-border",
    variant === "hero" && "rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90",
    variant === "faq" && "rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90",
    className
  );

  const handleMouseEnter = () => {
    triggerConfetti();
  };

  return (
    <Button 
      ref={buttonRef}
      onClick={handleSignIn}
      onMouseEnter={handleMouseEnter}
      disabled={isLoading}
      className={buttonClassName}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Github className="h-4 w-4" />
      )}
      {showLabelOnHover && (
        <span className={cn(
          "text-current ml-2",
          variant === "header" && "px-6"
        )}>
          {isLoading ? 'Signing in...' : 'Sign in with GitHub'}
        </span>
      )}
    </Button>
  );
}
